import { ExecutorActionSymbol, Point } from '../../helpers/constants';
import {
  MultimediaAndHintReviewActionPayload,
  ShowMultimediaAndHintReviewActionPayload,
} from '../../helpers/shared-actions.types';
import { CaptureBTPoint, IActionCreator } from '../../helpers/types';

// These actions are not thrown by the executor, but are necessary helpers
// for the lesson to be played.
export const types = {
  START_EXECUTOR: 'START_EXECUTOR',
  START_ATTEMPT: 'START_ATTEMPT',
  RECORD_ATTEMPT: 'RECORD_ATTEMPT',
  FINISH_ATTEMPT: 'FINISH_ATTEMPT',
  ABORT_AFTER_MAX_RETRIES: 'ABORT_AFTER_MAX_RETRIES',
  EXECUTOR_STATUS_UPDATE: 'EXECUTOR_STATUS_UPDATE',
  MULTIMEDIA_OK_CLICK: 'OK_CLICKED_ON_MULTIMEDIA',
  TAKE_NEXT_ACTION: 'TAKE_NEXT_ACTION',
  UI_WINDOW_BTN_CLICKED: 'UI_WINDOW_BTN_CLICKED',
  UI_TAB_CLICKED: 'UI_TAB_CLICKED',
  TOUCH_PANEL_BTN_CLICKED: 'TOUCH_PANEL_BTN_CLICKED',
  SEND_TO_PARENT: 'SEND_TO_PARENT',
  LISTEN_IN_TAB: 'LISTEN_IN_TAB',
  UPDATE_PENDING_ANIMATIONS: 'UPDATE_PENDING_ANIMATIONS',
  UI_USER_SET_WINDOW_ATTRIBUTE: 'UI_USER_SET_WINDOW_ATTRIBUTE',
  SHOW_HINT: 'ShowHint', //format similar to action dispatched by executor, resuing same action
  RESET_EXECUTOR_STATE: 'RESET_EXECUTOR_STATE',
  REVERT_TO_CHECKPOINT: 'REVERT_TO_CHECKPOINT',
  CLEAR_CHECKPOINT: 'CLEAR_CHECKPOINT',
  TOGGLE_BUTTON_CLICK: 'TOGGLE_BUTTON_CLICK',
  SHOW_INDICATOR: 'SHOW_INDICATOR',
  INTERNAL_SET_MONITORLAYOUT_CONFIG: 'INTERNAL_SET_MONITORLAYOUT_CONFIG',
  INTERNAL_SET_MAP: 'INTERNAL_SET_MAP',
  INTERNAL_SELECT_DEMO_POINT: 'INTERNAL_SELECT_DEMO_POINT',
  UI_BT_CAPTURE: 'UI_BT_CAPTURE',
  RANGE_IN: 'RANGE_IN',
  RANGE_OUT: 'RANGE_OUT',
  INTERNAL_SET_PRIMARY_HOOK: 'INTERNAL_SET_PRIMARY_HOOK',
  UI_SET_PRIMARY_HOOK: 'UI_SET_PRIMARY_HOOK',
  INTERNAL_SET_SECONDARY_HOOK: 'INTERNAL_SET_SECONDARY_HOOK',
  UI_SET_SECONDARY_HOOK: 'UI_SET_SECONDARY_HOOK',
  UPDATE_EXECUTION_MODE: 'UPDATE_EXECUTION_MODE',
  UPDATE_GENERAL_BUTTON_STATUS: 'UPDATE_GENERAL_BUTTON_STATUS',
  UPDATE_ANIMATED_TRACK_POSITION: 'UPDATE_ANIMATED_TRACK_POSITION',
  UPDATE_TSD_ILLUMINATION: 'UPDATE_TSD_ILLUMINATION',
  UPDATE_TSD_BLINKING: 'UPDATE_TSD_BLINKING',
  END_OF_LESSON: `END_OF_LESSON`,
  EXECUTOR_DELAY: `EXECUTOR_DELAY`,
  UPDATE_DIALOG_MONITOR: 'UPDATE_DIALOG_MONITOR',
  READY_TO_END_LESSON: `READY_TO_END_LESSON`,
  INTERNAL_MAP_LOADED: `INTERNAL_MAP_LOADED`,
  ADD_MULTIMEDIA_HINT_REVIEW: `ADD_MULTIMEDIA_HINT_REVIEW`,
  SHOW_MULTIMEDIA_HINT_REVIEW: `SHOW_MULTIMEDIA_HINT_REVIEW`,
  DISMISS_MULTIMEDIA_HINT_REVIEW: `DISMISS_MULTIMEDIA_HINT_REVIEW`,
} as const;

type IntExecutorActionType<Key extends keyof typeof types> =
  (typeof types)[Key];

export const executorDelay_AC = (time: number) => ({
  type: types.EXECUTOR_DELAY,
  payload: {
    time,
  },
});

export const endOfLesson_AC = (message = '') => ({
  type: types.END_OF_LESSON,
  payload: {
    message,
  },
});

export const readyToEndOfLesson_AC = (message = '') => ({
  type: types.READY_TO_END_LESSON,
  payload: {
    message,
  },
});

export const updateDialogMonitor_AC = (payload: { monitor: string }) => ({
  type: types.UPDATE_DIALOG_MONITOR,
  payload,
});

export const updateTSDBlinking_AC = (payload: any) => ({
  type: types.UPDATE_TSD_BLINKING,
  payload,
});

export const updateTSDIllumination_AC = (payload: any) => ({
  type: types.UPDATE_TSD_ILLUMINATION,
  payload,
});

export const updateAnimatedTrackPosition_AC = (
  position: Point,
  trackIdentifier: string
) => ({
  type: types.UPDATE_ANIMATED_TRACK_POSITION,
  payload: {
    position,
    trackIdentifier,
  },
});

export const updateExecutionMode_AC = (mode: string) => ({
  type: types.UPDATE_EXECUTION_MODE,
  payload: {
    mode,
  },
});

export const uiSetSecondaryHook = ({
  identifier,
  latitude,
  longitude,
  hookProperties,
}: {
  identifier: string;
  latitude: string;
  longitude: string;
  hookProperties: any;
}) => ({
  type: types.UI_SET_SECONDARY_HOOK,
  payload: {
    latitude,
    longitude,
    hookProperties,
    identifier,
  },
});

export const internalSetSecondaryHook_AC = ({
  identifier,
  latitude,
  longitude,
  hookProperties,
}: {
  identifier: string;
  latitude: string;
  longitude: string;
  hookProperties: any;
}) => ({
  type: types.INTERNAL_SET_SECONDARY_HOOK,
  payload: {
    latitude,
    longitude,
    hookProperties,
    identifier,
  },
});

export const uiSetPrimaryHook = ({
  identifier,
  latitude,
  longitude,
  hookProperties,
}: {
  identifier: string;
  latitude: string;
  longitude: string;
  hookProperties: any;
}) => ({
  type: types.UI_SET_PRIMARY_HOOK,
  payload: {
    latitude,
    longitude,
    hookProperties,
    identifier,
  },
});

export const internalSetPrimaryHook_AC = ({
  identifier,
  latitude,
  longitude,
  hookProperties,
}: {
  identifier: string;
  latitude: string;
  longitude: string;
  hookProperties: any;
}) => ({
  type: types.INTERNAL_SET_PRIMARY_HOOK,
  payload: {
    latitude,
    longitude,
    hookProperties,
    identifier,
  },
});

export const rangeIn_AC = ({ monitor }: { monitor: string }) => ({
  type: types.RANGE_IN,
  payload: {
    monitor,
  },
});

export const rangeOut_AC = ({ monitor }: { monitor: string }) => ({
  type: types.RANGE_OUT,
  payload: {
    monitor,
  },
});

export const uiBTCapture_AC = ({
  buttonId,
  captureAttributes,
  windowId,
}: {
  buttonId: string;
  captureAttributes: CaptureBTPoint;
  windowId: string;
}) => {
  return {
    type: types.UI_BT_CAPTURE,
    payload: {
      buttonId,
      captureAttributes,
      windowId,
    },
  };
};

export const internalSelectDemoPoint_AC = ({
  coordinate,
  buttonId,
}: {
  coordinate: any;
  buttonId: string;
}) => {
  return {
    type: types.INTERNAL_SELECT_DEMO_POINT,
    payload: {
      ...coordinate,
      buttonId,
    },
  };
};

export const internalSetMap_AC = ({
  mapContainerId,
  monitor,
  position,
  mapObj,
}: {
  mapContainerId: string;
  monitor: string;
  position: string;
  mapObj: any;
}) => {
  return {
    type: types.INTERNAL_SET_MAP,
    payload: {
      mapContainerId,
      monitor,
      position,
      mapObj,
    },
  };
};

export const internalSetMoinitorLayoutConfig_AC = (monitorLayout: any) => {
  return {
    type: types.INTERNAL_SET_MONITORLAYOUT_CONFIG,
    payload: {
      monitorLayout,
    },
  };
};

export const uiTabClicked_AC = (windowId: string, tabId: string) => {
  return {
    type: types.UI_TAB_CLICKED,
    payload: {
      windowId,
      tabId,
    },
  };
};

export const showIndicator_AC = (x: number, y: number) => {
  return {
    type: types.SHOW_INDICATOR,
    payload: {
      x,
      y,
    },
  };
};

export const toggleButtonClick_Ac = (key: string, value: string) => {
  return {
    type: types.TOGGLE_BUTTON_CLICK,
    payload: {
      key,
      value,
    },
  };
};

export const revertToCheckPoint_Ac = () => {
  return {
    type: types.REVERT_TO_CHECKPOINT,
  };
};

export const clearCheckPoint_Ac = () => {
  return {
    type: types.CLEAR_CHECKPOINT,
  };
};

export const resetExecutorState_Ac = () => {
  return {
    type: types.RESET_EXECUTOR_STATE,
  };
};

export const showHint_Ac = (hint: string, isError: boolean = false) => {
  return {
    type: types.SHOW_HINT,
    payload: {
      actionPayload: {
        hint,
        xPosition: 10,
        yPosition: 10,
        isError,
      },
    },
  };
};

export const showHintNoActionButtons_Ac = (hint: string) => {
  return {
    type: types.SHOW_HINT,
    payload: {
      actionPayload: {
        hint,
        xPosition: 10,
        yPosition: 10,
        noActionButtons: true,
      },
    },
  };
};

export const uiUserSetWindowAttribute_AC = (
  windowId: string,
  itemId: string,
  value: any
) => {
  if ('object' === typeof itemId) {
    return {
      type: types.UI_USER_SET_WINDOW_ATTRIBUTE,
      payload: {
        windowId,
        value: itemId,
      },
    };
  } else {
    return {
      type: types.UI_USER_SET_WINDOW_ATTRIBUTE,
      payload: {
        windowId,
        value: { [itemId]: value },
      },
    };
  }
};

export const updatePendingAnimations_AC = (
  count: any,
  isNonBlocking: boolean = false
) => {
  return {
    type: types.UPDATE_PENDING_ANIMATIONS,
    payload: {
      count,
      isNonBlocking,
    },
  };
};

export const sendToParent_AC = (payload: any) => {
  return {
    type: types.SEND_TO_PARENT,
    payload,
  };
};

export const touchPanelButtonClicked_AC = (
  buttonId: string,
  touchPanelId?: string
) => {
  return {
    type: types.TOUCH_PANEL_BTN_CLICKED,
    payload: {
      buttonId,
      touchPanelId,
    },
  };
};

export const listenInTab_AC = () => {
  return {
    type: types.LISTEN_IN_TAB,
  };
};

export const uiWindowBtnClickAC = (windowId: string, buttonId: string) => {
  return {
    type: types.UI_WINDOW_BTN_CLICKED,
    payload: {
      windowId,
      buttonId,
    },
  };
};

export const startExecutorAC = (trainingUnit: any, context: any) => {
  console.log('startExecutor ac');
  return {
    type: types.START_EXECUTOR,
    payload: {
      trainingUnit,
      context,
    },
  };
};

export const startAttemptAC = () => {
  return {
    type: types.START_ATTEMPT,
  };
};
export const recordAttemptAC = (payload) => {
  return {
    type: types.RECORD_ATTEMPT,
    payload,
  };
};

export const finishAttemptAC = (payload?: any) => ({
  type: types.FINISH_ATTEMPT,
  payload,
});

export const abortAfterMaxRetiesAC = () => ({
  type: types.ABORT_AFTER_MAX_RETRIES,
});

export const executorStatusUpdateAC = (status: string) => {
  return {
    type: types.EXECUTOR_STATUS_UPDATE,
    payload: {
      status,
    },
  };
};

export const createExecutorAction = (
  actionType: string,
  actionPayload: any
) => {
  return {
    type: actionType,
    payload: {
      actionPayload,
      [Symbol.for(ExecutorActionSymbol)]: true,
    },
  };
};

export const multimediaOkClickedAC = () => {
  return {
    type: types.MULTIMEDIA_OK_CLICK,
  };
};

export const takeNextActionAC = (reason: string) => {
  return {
    type: types.TAKE_NEXT_ACTION,
    payload: {
      meta: reason,
    },
  };
};

export const updateGeneralButtonStatus_Ac = (id: string, isActive: boolean) => {
  return {
    type: types.UPDATE_GENERAL_BUTTON_STATUS,
    payload: { id, isActive },
  };
};

export const internalMapLoaded_AC = (monitor: string) => {
  return {
    type: types.INTERNAL_MAP_LOADED,
    payload: { monitor },
  };
};

export const addMultimediaAndHintReview_AC: IActionCreator<
  IntExecutorActionType<'ADD_MULTIMEDIA_HINT_REVIEW'>,
  MultimediaAndHintReviewActionPayload
> = (payload: any) => {
  return {
    type: types.ADD_MULTIMEDIA_HINT_REVIEW,
    payload,
  };
};

export const showMultimediaAndHintReview_AC: IActionCreator<
  IntExecutorActionType<'SHOW_MULTIMEDIA_HINT_REVIEW'>,
  ShowMultimediaAndHintReviewActionPayload
> = (payload: Partial<ShowMultimediaAndHintReviewActionPayload>) => {
  return {
    type: types.SHOW_MULTIMEDIA_HINT_REVIEW,
    payload: {
      index: payload.index ?? -1,
    },
  };
};

export const dismissMultimediaAndHintReview_AC: IActionCreator<
  IntExecutorActionType<'DISMISS_MULTIMEDIA_HINT_REVIEW'>,
  {}
> = () => {
  return {
    type: types.DISMISS_MULTIMEDIA_HINT_REVIEW,
  };
};
